import { useRef, useState } from 'react';
import { Alert, Button, Form, OverlayTrigger, Popover, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { successMessage } from '@/utils/toast';
import { useValidateIntroCodeMutation } from '@/app/Purchase/api/ordersApi';
import {
  getIntroCodeDiscountAmount,
  getIntroCodeErrorFromCatch,
  getIntroCodeErrorMessage,
  isValidIntroCodeResponse,
} from '../../utils/introCode';
import type { IntroCodeFormProps } from '../../types/purchaseTypes';

function IntroCodeForm({
  isOpen,
  setIsOpen,
  planId,
  orderAmount,
  onSuccess,
}: IntroCodeFormProps) {
  const introCodeRef = useRef<HTMLInputElement | null>(null);
  const [introCodeError, setIntroCodeError] = useState('');
  const [validateIntroCode, { isLoading: isValidatingCode }] =
    useValidateIntroCodeMutation();

  const validationSchema = yup.object().shape({
    introCode: yup
      .string()
      .trim()
      .required('کدمعرف را وارد کنید.')
      .max(20, 'کدمعرف نباید بیشتر از ۲۰ کاراکتر باشد.'),
  });

  const handleSubmit = (
    values: { introCode: string },
    actions: { resetForm: () => void },
  ) => {
    const code = values.introCode.trim();
    setIntroCodeError('');

    if (!planId) {
      setIntroCodeError('شناسه پلن برای اعتبارسنجی کد یافت نشد.');
      return;
    }

    validateIntroCode({
      code,
      order_amount: Math.max(orderAmount, 0),
      plan_id: planId,
    })
      .unwrap()
      .then((res) => {
        if (isValidIntroCodeResponse(res)) {
          successMessage('کدمعرف با موفقیت اعمال شد');
          onSuccess?.({
            ...res,
            data: {
              ...res.data,
              code,
              discount_amount: getIntroCodeDiscountAmount(res.data, orderAmount),
            },
          });
          setIsOpen(false);
          actions.resetForm();
          return;
        }

        setIntroCodeError(
          getIntroCodeErrorMessage(res, 'کدمعرف معتبر نمیباشد.'),
        );
      })
      .catch((err) => {
        setIntroCodeError(
          getIntroCodeErrorFromCatch(
            err,
            'در اعتبارسنجی کد تخفیف خطایی رخ داد.',
          ),
        );
        console.error(err);
      });
  };

  return (
    <OverlayTrigger
      trigger="click"
      rootClose
      show={isOpen}
      placement="top"
      overlay={
        <Popover id="popover-basic" style={{ width: '350px' }}>
          <Popover.Header
            as="h3"
            className="d-flex justify-content-between align-items-center"
          >
            کدمعرف رو وارد کنید
          </Popover.Header>
          <Popover.Body className="px-3 py-2">
            <Formik
              initialValues={{ introCode: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                touched,
                errors,
                resetForm,
              }) => (
                <Form
                  onSubmit={(event) => {
                    setIntroCodeError('');
                    handleSubmit(event);
                  }}
                >
                  <Form.Group className="my-3">
                    <Form.Control
                      autoFocus
                      ref={introCodeRef}
                      size="sm"
                      onChange={(event) => {
                        setIntroCodeError('');
                        handleChange(event);
                      }}
                      value={values.introCode}
                      isInvalid={
                        Boolean(introCodeError) ||
                        (touched.introCode && Boolean(errors.introCode))
                      }
                      isValid={touched.introCode && !errors.introCode && !introCodeError}
                      name="introCode"
                      onBlur={handleBlur}
                      placeholder="کدمعرف رو اینجا وارد کن..."
                    />
                    <Form.Control.Feedback
                      type={errors.introCode || introCodeError ? 'invalid' : 'valid'}
                    >
                      {touched.introCode && errors.introCode}
                    </Form.Control.Feedback>
                    {introCodeError ? (
                      <Alert
                        variant="danger"
                        className="mb-0 mt-2 px-2 py-1 fs--2"
                        role="alert"
                      >
                        {introCodeError}
                      </Alert>
                    ) : null}
                  </Form.Group>

                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      size="sm"
                      variant="falcon-primary"
                      type="submit"
                      disabled={isValidatingCode}
                    >
                      {!isValidatingCode ? (
                        'اعمال'
                      ) : (
                        <>
                          <Spinner
                            style={{ width: '16px', height: '16px' }}
                            className="me-1"
                          />
                          درحال بررسی کد‌معرف
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setIsOpen(false);
                        setIntroCodeError('');
                        resetForm();
                      }}
                    >
                      بستن
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Popover.Body>
        </Popover>
      }
    >
      <Button
        onClick={() => {
          setIsOpen(!isOpen);
          setIntroCodeError('');
          introCodeRef?.current?.focus();
        }}
        className="w-100"
        variant="secondary"
      >
        کدمعرف
      </Button>
    </OverlayTrigger>
  );
}

export default IntroCodeForm;
